import { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { FormControl } from '@mui/material';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import ButtonSubmitData from 'shared/components/Button/ButtonSubmitData/ButtonSubmitData';
import InputForm from 'shared/components/Input/InputForm';
import Loader from 'shared/containers/Loader';
import { GENERAL_INFO_TITLE } from 'shared/containers/ViewDetails/ViewDetails';
import { APIErrorT } from 'shared/models/api';
import { SourcesResultT } from 'shared/models/sources';
import { SourcesIdentifiers } from 'shared/models/sources-form';
import api from 'utils/api';

import './SourcesManageData.scss';

import SourcesManageDataValidator from './SourcesManageData.validator';
import SourcesManageDataForm from './SourcesManageDataForm';

type FormSpecificValuesT = {
  headers?: Record<string, string>;
  url?: string;
  body?: string;
  apiKey?: string;
  ca?: string;
  crt?: string;
  username?: string;
  password?: string;
  downlinkAddress?: string;
  topic?: string;
  keepAlive?: string;
  clientID?: string;
}

export type FormValuesT = {
  name?: string;
  mtuTxLink?: number;
  useDefaultMtu?: boolean;
  // sourcePath: From defaultValues API Call not displayed
  // to the user but need to be in the form request
  sourcePath?: string;
  specific: FormSpecificValuesT;
}

function SourcesManageData() {
  const params: any = useParams();
  const [selectedSource, setSelectedSource] = useState('');
  const [sourceDetails, setSourceDetails] = useState<
    SourcesResultT | any | null
  >(null);
  const [sourcesNames, setSourcesNames] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const history = useHistory();
  const methods = useForm<FormValuesT>({
    mode: 'onChange',
    resolver: yupResolver(SourcesManageDataValidator),
  });
  const {
    handleSubmit,
    register,
    formState: {
      errors,
    },
  } = methods;

  const handleSourceChange = (event: any) => {
    setSelectedSource('');
    setSelectedSource(event.target.value);
  };

  const isEdit = params.id !== undefined;

  const onSubmit: SubmitHandler<any> = (formData: FormValuesT) => {
    setIsSubmitting(true);
    const specificName = SourcesIdentifiers[selectedSource].parametersGroupName ?? 'undefined_specific';
    const newData = {
      ...sourceDetails,
      source: selectedSource,
      name: formData.name,
      mtuTxLink: formData.mtuTxLink,
      useDefaultMtu: formData.useDefaultMtu,
      sourcePath: formData.sourcePath,
      [specificName]: formData.specific,
    };
    const request = isEdit
      ? api.sources.update(sourceDetails?.id as string, newData)
      : api.sources.register(newData);
    request
      .then((resp: any) => {
        if (resp.status >= 400) {
          resp.json()
            .then((error: APIErrorT) => {
              toast.error(error?.message);
              setIsSubmitting(false);
            });
        } else {
          resp.json()
            .then(() => {
              toast.success(`Source of type ${SourcesIdentifiers[selectedSource].displayName} has been ${isEdit ? 'updated' : 'created'}`);
              history.push(isEdit ? `/sources/${sourceDetails?.id}` : '/sources');
              setIsSubmitting(false);
            });
        }
      });
  };

  const getAvailableSourceNames = () => {
    setIsLoading(true);
    api.sources.getAvailablesSourceNames()
      .then((resp: any) => {
        if (resp.status >= 400) {
          resp.json()
            .then((error: APIErrorT) => {
              toast.error(error?.message);
            });
        } else {
          resp.json()
            .then((data: any) => {
              setSourcesNames(data.sources);
              setIsLoading(false);
            });
        }
      });
  };

  const loadSourceData = () => {
    api.sources.getOne(params.id)
      .then((resp: any) => {
        if (resp.status >= 400) {
          resp.json()
            .then((error: APIErrorT) => {
              toast.error(error?.message);
            });
        } else {
          resp.json()
            .then((data: SourcesResultT) => {
              setSourceDetails(data);
              setSelectedSource(data.source);
            });
        }
      });
  };

  useEffect(() => {
    if (isEdit) {
      loadSourceData();
    }
    getAvailableSourceNames();
  }, []);

  if (isLoading) {
    return <Loader.Page />;
  }
  return (
    <FormProvider {...methods}>
      <form className="form" onSubmit={handleSubmit(onSubmit)}>
        <div className="title__header">
          <h1 className="">Source · {isEdit ? 'Update' : 'Create'}</h1>
          { selectedSource
            ? (
              <ButtonSubmitData
                isEdit={isEdit}
                isSubmitting={isSubmitting}
              />
            )
            : null}
        </div>
        <div className="scroll">
          <div className="form__section">
            <h3>{ GENERAL_INFO_TITLE }</h3>
            <InputForm.TextField
              fullWidth
              sx={{ mt: 1, mb: 1 }}
              label="Name"
              register={register('name')}
              errorField={errors.name}
            />
            <FormControl fullWidth sx={{ mt: 1, mb: 1 }}>
              <InputLabel>Select your source</InputLabel>
              <Select
                name="source"
                label="Select your source"
                onChange={handleSourceChange}
                value={selectedSource}
                disabled={isEdit}
              >
                {
                  sourcesNames.map((source) => (
                    <MenuItem
                      key={source}
                      value={source}
                    >
                      {
                        SourcesIdentifiers[source]
                          ? SourcesIdentifiers[source].displayName
                          : source
                      }
                    </MenuItem>
                  ))
                }
              </Select>
            </FormControl>
          </div>
          {
          selectedSource
            ? (
              <SourcesManageDataForm name={selectedSource} currentValues={sourceDetails} />
            )
            : null
        }
        </div>
      </form>
    </FormProvider>
  );
}

export default SourcesManageData;
